import React, { useMemo, useState, useContext, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import GeneralContext from "../GeneralContext/GeneralContext";

import PageBody from "../GeneralComponents/PageBody";
import BreadcrumbStandard from "../GeneralComponents/BreadcrumbStandard";
import PageBgBlock from "../GeneralComponents/PageBgBlock";
import InputFile from "../GeneralComponents/InputFile";
import PageButtonBlack from "../GeneralComponents/PageButtonBlack";
import PageButtonWhite from "../GeneralComponents/PageButtonWhite";

import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import Alert from "@mui/material/Alert";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import HourglassLoader from "../GeneralComponents/HourglassLoader";
import axios from "../../api/axios";

/* ===================== Constantes dominio ===================== */

const TECNOLOGIAS = [
  "Fibra Óptica",
  "SATELITAL",
  "Satelital de órbita baja",
  "ADSL_HFC",
  "HFC & FTTH",
  "HFC",
  "FTTH",
  "4G",
  "MICROONDAS",
  "VSAT",
];

const OPERADORES = {
  "LUZLINARES S.A.": 1,
  "MI INTERNET SPA": 2,
  "ELECTRONET S.A.": 3,
  "GTD MANQUEHUE S.A.": 4,
  "INGBELL CHILE SPA": 5,
  "CHILE TV CABLE S.A.": 6,
  "NETLAND CHILE S.A.": 7,
  "TELEFONICA EMPRESAS CHILE S.A.": 8,
  "LUZPARRAL S.A.": 11,
  "TEXUS NETWORKS SPA.": 19,
  "VTR COMUNICACIONES SPA.": 20, // ajusta si es otro ID
};

/* ===================== Esquema ===================== */

const SCHEMA = {
  columns: [
    { key: "convocatoria", label: "CONVOCATORIA", type: "string", required: true },

    { key: "año", label: "AÑO", type: "integer", required: true, min: 2024, max: 2030 },

    {
      key: "operador",
      label: "OPERADOR",
      type: "string",
      required: true,
      enum: Object.keys(OPERADORES),
      maxLength: 80,
    },

    { key: "rbd", label: "RBD", type: "integer", required: true, min: 1, max: 99999 },

    {
      key: "nombre_establecimiento",
      label: "NOMBRE ESTABLECIMIENTO",
      type: "string",
      required: true,
      maxLength: 120,
    },

    {
      key: "tipo_establecimiento",
      label: "TIPO ESTABLECIMIENTO",
      type: "string",
      required: true,
      enum: [
        "ADMINISTRACION DELEGADA",
        "MUNICIPAL DAEM",
        "PARTICULAR SUBVENCIONADO",
        "SERVICIO LOCAL DE EDUCACIÓN (SLE)",
        "SERVICIO LOCAL DE EDUCACIÓN PÚBLICA (SLEP)",
      ],
      maxLength: 120,
    },

    {
      key: "rut_sostenedor",
      label: "RUT SOSTENEDOR",
      type: "string",
      required: true,
      pattern: "^[0-9]{7,9}(-[0-9Kk])?$",
      maxLength: 12,
    },

    {
      key: "nombre_sostenedor",
      label: "NOMBRE SOSTENEDOR",
      type: "string",
      required: true,
      maxLength: 120,
    },

    { key: "region", label: "REGION", type: "string", required: true, maxLength: 40 },

    { key: "comuna", label: "COMUNA", type: "string", required: true, maxLength: 60 },

    {
      key: "direccion",
      label: "DIRECCIÓN",
      type: "string",
      required: true,
      maxLength: 140,
    },

    {
      key: "tipo_de_area",
      label: "TIPO DE AREA",
      type: "string",
      required: true,
      enum: ["URBANA", "RURAL"],
    },

    { key: "latitud", label: "LATITUD", type: "number", required: true, min: -90, max: 90 },

    { key: "longitud", label: "LONGITUD", type: "number", required: true, min: -180, max: 180 },

    { key: "zona", label: "ZONA", type: "integer", required: false, min: 0 },

    {
      key: "matricula_total",
      label: "MATRICULA TOTAL",
      type: "integer",
      required: false,
      min: 0,
    },

    {
      key: "jornada",
      label: "JORNADA (VENTANA HORARIA)",
      type: "string",
      required: true,
      maxLength: 80,
      enum: ["A", "B", "C", "D", "E"],
    },

    {
      key: "bajada_total_mbps",
      label: "DE BAJADA TOTAL [Mbps]",
      type: "number",
      required: true,
      min: 0,
    },

    {
      key: "subida_total_mbps",
      label: "DE SUBIDA TOTAL [Mbps]",
      type: "number",
      required: true,
      min: 0,
    },

    {
      key: "bajada_internacional_mbps",
      label: "DE BAJADA INTERNACIONAL [Mbps]",
      type: "number",
      required: true,
      min: 0,
    },

    {
      key: "disponibilidad_mensual",
      label: "Disponibilidad mensual",
      type: "number",
      required: true,
      min: 0,
      max: 100,
    },

    {
      key: "tipo_tecnologia",
      label: "Tipo Tecnología",
      type: "string",
      required: true,
      enum: TECNOLOGIAS,
    },

    {
      key: "subsidio_anual",
      label: "Subsidio anual",
      type: "integer",
      required: true,
      min: 0,
    },

    {
      key: "subsidio_mensualizado",
      label: "Subsidio mensualizado",
      type: "integer",
      required: true,
      min: 0,
    },

    {
      key: "telefono",
      label: "teléfono",
      type: "string",
      required: false,
      maxLength: 20,
      pattern: "^\\+?[0-9]{6,15}$",
    },

    {
      key: "email",
      label: "email",
      type: "string",
      required: false,
      maxLength: 120,
      pattern: "^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$",
    },
  ],
};

/* ===================== Best match OPERADOR basado en SCHEMA ===================== */

const OPERADOR_COL = SCHEMA.columns.find((c) => c.key === "operador");
const ALLOWED_OPERADORES = OPERADOR_COL?.enum ?? [];

function normalizeOperatorName(str) {
  if (!str) return "";
  return String(str)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") 
    .toUpperCase()
    .replace(/[.,]/g, " ") // sacar puntos y comas
    .replace(/\b(SA|S\.A\.|SPA|S\.P\.A\.|LTDA|LIMITADA)\b/g, "") // suffix típicos
    .replace(/\s+/g, " ")
    .trim();
}

function findBestOperatorMatch(raw) {
  if (!raw) return raw;

  const norm = normalizeOperatorName(raw);
  if (!norm) return raw;

  let bestOp = null;
  let bestScore = 0;

  for (const op of ALLOWED_OPERADORES) {
    const normOp = normalizeOperatorName(op);
    let score = 0;

    if (norm === normOp) {
      score = 1;
    } else if (normOp.includes(norm) || norm.includes(normOp)) {
      score = 0.9;
    } else {
      const tokens = norm.split(" ");
      const opTokens = normOp.split(" ");
      const common = tokens.filter((t) => opTokens.includes(t));
      if (common.length > 0) {
        score = common.length / Math.max(tokens.length, opTokens.length);
      }
    }

    if (score > bestScore) {
      bestScore = score;
      bestOp = op;
    }
  }

  // umbral para evitar matches muy malos
  if (bestScore >= 0.4 && bestOp) {
    return bestOp;
  }

  return raw;
}


const BEST_MATCHERS = {
  operador: findBestOperatorMatch,
};

/* ===================== Helpers ===================== */

function trimEdges(val) {
  if (val === null || val === undefined) return val;
  if (typeof val === "number") return val;
  return String(val).trim();
}

function parseNumericFlexible(raw) {
  if (raw === null || raw === undefined) return { value: raw };
  let s = String(raw).trim();
  s = s.replace(/^\s*\$/g, "").replace(/%/g, "").replace(/\s+/g, "");
  if (s.includes(".") && s.includes(",")) {
    s = s.replace(/\./g, "").replace(",", ".");
  } else if (s.includes(",") && !s.includes(".")) {
    s = s.replace(",", ".");
  }
  s = s.replace(/'/g, "");
  const num = Number(s);
  if (Number.isNaN(num)) return { value: raw, error: "Debe ser numérico" };
  return { value: num };
}

function parseIntegerFlexible(raw) {
  if (raw === null || raw === undefined) return { value: raw };
  let s = String(raw).trim();
  s = s.replace(/^\s*\$/g, "").replace(/%/g, "").replace(/\s+/g, "");
  s = s.replace(/[.,' ]/g, "");
  const m = s.match(/^\d+$/);
  if (!m) return { value: raw, error: "Debe ser entero" };
  return { value: Number(s) };
}

function coerce(val, type) {
  if (type === "integer") {
    const { value, error } = parseIntegerFlexible(val);
    if (error) return { value, error };
    return { value };
  }
  if (type === "number") {
    const { value, error } = parseNumericFlexible(val);
    if (error) return { value, error };
    return { value };
  }
  return { value: trimEdges(val) };
}

function validateCell(raw, col) {
  const errors = [];
  const isEmpty = raw === null || raw === undefined || String(raw).trim() === "";
  if (col.required && isEmpty) {
    errors.push("Requerido");
    return errors;
  }
  if (isEmpty) return errors;

  const { value, error } = coerce(raw, col.type);
  if (error) errors.push(error);

  if (col.type === "string") {
    const len = String(value).length;
    if (col.minLength != null && len < col.minLength) errors.push(`Mín ${col.minLength} car.`);
    if (col.maxLength != null && len > col.maxLength) errors.push(`Máx ${col.maxLength} car.`);
  }
  if (col.type === "number" || col.type === "integer") {
    const num = Number(value);
    if (col.min != null && num < col.min) errors.push(`Mín ${col.min}`);
    if (col.max != null && num > col.max) errors.push(`Máx ${col.max}`);
  }
  if (col.enum && col.enum.length > 0) {
    const allowed = col.enum.map(String);
    if (!allowed.includes(String(value))) errors.push("Valor no permitido");
  }
  if (col.pattern) {
    try {
      if (!new RegExp(col.pattern).test(String(value))) errors.push("Formato inválido");
    } catch {
      // patrón inválido, lo ignoramos
    }
  }
  return errors;
}

function normalizeHeader(s) {
  if (s == null) return "";
  const noAcc = s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const flat = noAcc.replace(/\s+/g, " ").trim().toUpperCase();
  return flat;
}

function pickRowBySchema(row, schema) {
  const normRow = {};
  for (const [k, v] of Object.entries(row)) {
    normRow[normalizeHeader(k)] = v;
  }
  const out = {};
  for (const c of schema.columns) {
    const normLabel = normalizeHeader(c.label);
    out[c.key] = Object.prototype.hasOwnProperty.call(normRow, normLabel)
      ? normRow[normLabel]
      : "";
  }
  return out;
}

function castRowTypes(row, schema) {
  const out = { ...row };
  for (const col of schema.columns) {
    if (col.type === "number" || col.type === "integer") {
      const { value } = coerce(out[col.key], col.type);
      out[col.key] = value;
    } else if (col.type === "string") {
      out[col.key] = trimEdges(out[col.key]);
    }

    // aplicar best match si está configurado para este key
    if (BEST_MATCHERS[col.key]) {
      out[col.key] = BEST_MATCHERS[col.key](out[col.key]);
    }
  }
  return out;
}

function validateRow(row, schema) {
  const e = {};
  for (const col of schema.columns) {
    const list = validateCell(row[col.key], col);
    if (list.length) e[col.key] = list;
  }
  return e;
}

const isEnumCol = (col) => Array.isArray(col.enum) && col.enum.length > 0;

const numericKeys = new Set([
  "año",
  "rbd",
  "latitud",
  "longitud",
  "matricula_total",
  "bajada_total_mbps",
  "subida_total_mbps",
  "bajada_internacional_mbps",
  "disponibilidad_mensual",
  "subsidio_anual",
  "subsidio_mensualizado",
  "zona",
]);

const isNumberColByKey = (k) => numericKeys.has(k);
const isNumberCol = (col) => col.type === "number" || col.type === "integer";

/* ===================== Componente ===================== */

export default function ParqueNYA() {
  const fileRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const [missingCols, setMissingCols] = useState([]);
  const [fileError, setFileError] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // "success" | "error" | null
  const [submitMessage, setSubmitMessage] = useState("");
  const [apiResult, setApiResult] = useState(null);

  const { setDrawerHidding } = useContext(GeneralContext);

  const links = [
    { name: "Estado de Parque", path: "/parque_lista" },
    { name: "Agregar RBD", path: "/parque_nya" },
  ];

  const allValid = useMemo(
    () => rows.length > 0 && errors.every((e) => Object.keys(e).length === 0),
    [rows, errors]
  );

  const openUrl = (url) => {
    if (!url || url === "#") return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  async function handleFile(file) {
    if (!file) {
      setRows([]);
      setErrors([]);
      setMissingCols([]);
      setFileError(null);
      return;
    }

    setSubmitStatus(null);
    setSubmitMessage("");
    setApiResult(null);
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false });

      if (!json || json.length === 0) {
        setRows([]);
        setErrors([]);
        setMissingCols([]);
        setFileError(
          "No se pudo reconocer el contenido del archivo. Verifique que la hoja tenga encabezados y al menos una fila de datos."
        );
        return;
      }

      const headersInFile = new Set(Object.keys(json?.[0] ?? {}).map((h) => String(h).trim()));
      const miss = [];
      SCHEMA.columns.forEach((c) => {
        if (!headersInFile.has(c.label) && !headersInFile.has(c.key)) {
          miss.push(`${c.label} (${c.key})`);
        }
      });

      setMissingCols(miss);

      const recognizedCount = SCHEMA.columns.length - miss.length;
      if (recognizedCount === 0) {
        setRows([]);
        setErrors([]);
        setFileError(
          "No se pudo reconocer la estructura del archivo. Revise que los nombres de las columnas coincidan con la plantilla esperada."
        );
        return;
      }

      const picked = json.map((r) => castRowTypes(pickRowBySchema(r, SCHEMA), SCHEMA));
      const errs = picked.map((r) => validateRow(r, SCHEMA));

      setRows(picked);
      setErrors(errs);
      setFileError(null);
      setSubmitStatus(null);
      setSubmitMessage("");
      setApiResult(null);
    } catch (err) {
      console.error(err);
      setRows([]);
      setErrors([]);
      setMissingCols([]);
      setFileError(
        "No se pudo leer el archivo. Asegúrese de que sea un .xlsx o .csv con la estructura correcta."
      );
      setSubmitStatus("error");
      setSubmitMessage("Error al leer el archivo.");
      setApiResult(null);
    }
  }

  function handleCellChange(rowIdx, colKey, rawValue) {
    setRows((prev) => {
      const next = [...prev];
      const colDef = SCHEMA.columns.find((c) => c.key === colKey);

      let newVal = rawValue;
      if (colDef) {
        if (colDef.type === "integer") {
          const { value } = parseIntegerFlexible(rawValue);
          newVal = value;
        } else if (colDef.type === "number") {
          const { value } = parseNumericFlexible(rawValue);
          newVal = value;
        } else if (colDef.type === "string") {
          newVal = trimEdges(rawValue);
        }

        // aplicar best match si está configurado para este campo (ej. operador)
        if (BEST_MATCHERS[colKey]) {
          newVal = BEST_MATCHERS[colKey](newVal);
        }
      }

      next[rowIdx] = { ...next[rowIdx], [colKey]: newVal };

      setErrors((prevErrs) => {
        const nextErrs = [...prevErrs];
        nextErrs[rowIdx] = validateRow(next[rowIdx], SCHEMA);
        return nextErrs;
      });

      return next;
    });
  }

  async function handleSubmit() {
    const cleaned = rows.map((row) => {
      const out = { ...row };
      for (const col of SCHEMA.columns) {
        if (col.type === "string") {
          out[col.key] = trimEdges(out[col.key]);
        } else if (col.type === "integer") {
          const { value } = parseIntegerFlexible(out[col.key]);
          out[col.key] = value;
        } else if (col.type === "number") {
          const { value } = parseNumericFlexible(out[col.key]);
          out[col.key] = value;
        }

        if (BEST_MATCHERS[col.key]) {
          out[col.key] = BEST_MATCHERS[col.key](out[col.key]);
        }
      }

      if (out.operador) {
        out.id_operador = OPERADORES[out.operador] ?? null;
      }

      return out;
    });

    const revalidated = cleaned.map((r) => validateRow(r, SCHEMA));
    setRows(cleaned);
    setErrors(revalidated);

    const allOk = revalidated.every((e) => Object.keys(e).length === 0);
    if (!allOk) {
      alert("Aún hay celdas con error. Revisa los campos marcados en rojo.");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setSubmitMessage("");
    setApiResult(null);

    try {
      const response = await axios.addNya(cleaned);

      console.log("Respuesta addNya:", response);

      let payload = response?.data ?? response;
      if (typeof payload === "string") {
        try {
          payload = JSON.parse(payload);
        } catch {
          /* lo dejamos como string */
        }
      }

      console.log("Payload normalizado:", payload);

      setSubmitStatus("success");
      setSubmitMessage(`Se cargaron ${cleaned.length} filas correctamente.`);
      setApiResult(payload || {});

      // limpiar tabla y archivo
      setRows([]);
      setErrors([]);
      setMissingCols([]);
      setFileError(null);
      if (fileRef.current && typeof fileRef.current.clear === "function") {
        fileRef.current.clear();
      }
    } catch (error) {
      console.error(error);
      let msg = "Ocurrió un error al enviar los datos.";
      if (error?.response?.data?.detail) {
        msg = error.response.data.detail;
      }
      setSubmitStatus("error");
      setSubmitMessage(msg);
    } finally {
      setIsSubmitting(false);
    }
  }

  useEffect(() => {
    setDrawerHidding(false);
  }, [setDrawerHidding]);

  const apiResultRows = [
    { key: "rbd_recibidas", label: "RBD recibidas" },
    { key: "rbd_normalizadas", label: "RBD normalizadas" },
    { key: "added_ees", label: "Establecimientos agregados (EES)" },
    {
      key: "added_servicios_conectividad_pactado",
      label: "Servicios conectividad pactado",
    },
    { key: "added_subsidios_cpe", label: "Subsidios CPE" },
    { key: "added_servicios_conectividad", label: "Servicios conectividad" },
    { key: "added_operadores_zonas", label: "Operadores zonas" },
    { key: "added_concurso_cpe", label: "Concurso CPE" },
  ];

  const hasApiResult =
    apiResult && typeof apiResult === "object" && Object.keys(apiResult).length > 0;

  return (
    <PageBody>
      <BreadcrumbStandard links={links} />

      {/* Cabecera + selector de archivo */}
      <PageBgBlock className="card">
        <Stack alignItems="left" justifyContent="center" spacing={2}>
          <Typography
            variant="h1"
            color="inherit"
            noWrap
            sx={{ flexGrow: 1, textAlign: "center", fontWeight: 700 }}
          >
            AGREGAR RBD
          </Typography>

          <Typography
            variant="body1"
            align="center"
            sx={{ pb: "2rem", color: "text.secondary" }}
          >
            En el siguiente campo suba el archivos con toda la información requerida por SAGEC
            para cada RBD, así estos quedarán ingresados para todos los procesos. Su extensión
            debe ser .xlsx o .csv.
          </Typography>

          {/* Link centrado */}
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Link
              component="button"
              type="button"
              sx={{ fontWeight: 600, pb: "3px" }}
              onClick={() => openUrl("google.cl")}
            >
              Descargar template de archivo base .xlsx
            </Link>
          </Box>

          <Box sx={{ border: "2px dashed #d1d5db", p: 1, borderRadius: 1 }}>
            <InputFile
              ref={fileRef}
              onFileSelect={(archivo) => handleFile(archivo)}
              fileType="sheet"
              placeholder="Subir archivo"
              buttonText="Subir archivo"
              sizeBtnGrid={4}
            />
          </Box>

          {fileError && <Alert severity="error">{fileError}</Alert>}
        </Stack>
      </PageBgBlock>

      {/* Loader mientras se ejecuta axios */}
      {isSubmitting && (
        <Box sx={{ pt: 3 }}>
          <PageBgBlock>
            <HourglassLoader />
          </PageBgBlock>
        </Box>
      )}

      {/* Bloque de éxito (independiente de rows.length) */}
      {!isSubmitting && submitStatus === "success" && (
        <Box sx={{ pt: 3 }}>
          <PageBgBlock>
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              onClose={() => {
                setSubmitStatus(null);
                setSubmitMessage("");
                setApiResult(null);
              }}
            >
              {submitMessage || "Carga realizada correctamente."}
            </Alert>

            {hasApiResult && (
              <>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Resumen de procesamiento
                </Typography>

                <Table size="small">
                  <TableBody>
                    {apiResultRows.map((row) => (
                      <TableRow key={row.key}>
                        <TableCell sx={{ fontWeight: 500 }}>{row.label}</TableCell>
                        <TableCell align="right">{apiResult[row.key] ?? 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </PageBgBlock>
        </Box>
      )}

      {/* Vista preliminar del Excel */}
      {!isSubmitting && rows.length > 0 && (
        <Box sx={{ pt: 3 }}>
          <PageBgBlock>
            <Typography
              variant="body1"
              align="center"
              sx={{ py: "2rem", px: "2rem", color: "text.secondary" }}
            >
              Esta es una vista preliminar del archivo cargado,{" "}
              <b>corrija los campos marcados en rojo</b> para habilitar el botón de ‘Cargar
              archivo’.
            </Typography>

            {submitStatus === "error" && submitMessage && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                onClose={() => {
                  setSubmitStatus(null);
                  setSubmitMessage("");
                }}
              >
                {submitMessage}
              </Alert>
            )}

            {missingCols.length > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                El .xlsx cargado no tiene las columnas: {missingCols.join(", ")}.
              </Alert>
            )}

            <Box sx={{ width: "100%", maxWidth: "100%", overflowX: "auto" }}>
              <TableContainer
                component={Paper}
                sx={{
                  width: "100%",
                  maxWidth: "100%",
                  overflowX: "auto",
                  maxHeight: "60vh",
                  borderRadius: 2,
                  border: "1px solid #e5e7eb",
                }}
              >
                <Table
                  size="small"
                  stickyHeader
                  sx={{
                    tableLayout: "auto",
                    width: "max-content",
                    maxWidth: "none",
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={(theme) => ({
                          position: "sticky",
                          top: 0,
                          left: 0,
                          zIndex: 4,
                          backgroundColor: theme.palette.background.paper,
                          width: 48,
                          textAlign: "center",
                          borderRight: "1px solid #eee",
                          boxShadow: "inset -6px 0 6px -6px rgba(0,0,0,0.12)",
                        })}
                      />
                      {SCHEMA.columns.map((c) => (
                        <TableCell
                          key={c.key}
                          align={isNumberColByKey(c.key) ? "right" : "left"}
                          sx={{
                            fontWeight: 600,
                            backgroundColor: "grey.50",
                          }}
                        >
                          {c.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {rows.map((r, idx) => {
                      const rowErrs = errors[idx] || {};
                      const ok = Object.keys(rowErrs).length === 0;

                      return (
                        <TableRow key={idx} hover>
                          <TableCell
                            sx={(theme) => ({
                              position: "sticky",
                              left: 0,
                              zIndex: 3,
                              backgroundColor: theme.palette.background.paper,
                              width: 48,
                              textAlign: "center",
                              borderRight: "1px solid #eee",
                              boxShadow: "inset -6px 0 6px -6px rgba(0,0,0,0.08)",
                            })}
                            title={ok ? "Fila válida" : "Fila con errores"}
                          >
                            {ok ? (
                              <CheckCircleIcon sx={{ color: "success.main", fontSize: 20 }} />
                            ) : (
                              <ErrorIcon sx={{ color: "error.main", fontSize: 20 }} />
                            )}
                          </TableCell>

                          {SCHEMA.columns.map((c) => {
                            const hasError = !!rowErrs[c.key];
                            const tooltip = hasError ? rowErrs[c.key].join(" · ") : "";
                            const raw = r[c.key];
                            const colDef = c;
                            const showError = hasError;
                            const helper = showError ? tooltip : "";
                            const commonSx = { minWidth: 140 };
                            const editable = true;

                            return (
                              <TableCell
                                key={c.key}
                                align={isNumberColByKey(c.key) ? "right" : "left"}
                                sx={(theme) => ({
                                  p: "2px 8px",
                                  whiteSpace: "nowrap",
                                  ...(hasError
                                    ? {
                                        backgroundColor:
                                          theme.palette.error.main + "14",
                                        outline: `1px solid ${theme.palette.error.main}59`,
                                        outlineOffset: "-1px",
                                      }
                                    : null),
                                })}
                              >
                                {editable ? (
                                  (() => {
                                    if (isEnumCol(colDef)) {
                                      return (
                                        <Tooltip
                                          title={helper}
                                          arrow
                                          placement="top"
                                          disableHoverListener={!showError}
                                        >
                                          <span>
                                            <Select
                                              size="small"
                                              fullWidth
                                              displayEmpty
                                              value={raw ?? ""}
                                              onChange={(e) =>
                                                handleCellChange(idx, c.key, e.target.value)
                                              }
                                              error={showError}
                                              sx={commonSx}
                                              disabled={isSubmitting}
                                            >
                                              <MenuItem value="">
                                                <em>—</em>
                                              </MenuItem>
                                              {colDef.enum.map((opt) => (
                                                <MenuItem key={String(opt)} value={opt}>
                                                  {String(opt)}
                                                </MenuItem>
                                              ))}
                                            </Select>
                                          </span>
                                        </Tooltip>
                                      );
                                    }

                                    if (colDef.type === "integer") {
                                      return (
                                        <Tooltip
                                          title={helper}
                                          arrow
                                          placement="top"
                                          disableHoverListener={!showError}
                                        >
                                          <TextField
                                            size="small"
                                            fullWidth
                                            value={raw ?? ""}
                                            onChange={(e) =>
                                              handleCellChange(idx, c.key, e.target.value)
                                            }
                                            inputProps={{
                                              inputMode: "numeric",
                                              pattern: "[0-9]*",
                                            }}
                                            error={showError}
                                            helperText={showError ? helper : ""}
                                            sx={commonSx}
                                            disabled={isSubmitting}
                                          />
                                        </Tooltip>
                                      );
                                    }

                                    if (colDef.type === "number") {
                                      return (
                                        <Tooltip
                                          title={helper}
                                          arrow
                                          placement="top"
                                          disableHoverListener={!showError}
                                        >
                                          <TextField
                                            size="small"
                                            fullWidth
                                            value={raw ?? ""}
                                            onChange={(e) =>
                                              handleCellChange(idx, c.key, e.target.value)
                                            }
                                            inputProps={{
                                              inputMode: "decimal",
                                            }}
                                            error={showError}
                                            helperText={showError ? helper : ""}
                                            sx={commonSx}
                                            disabled={isSubmitting}
                                          />
                                        </Tooltip>
                                      );
                                    }

                                    return (
                                      <Tooltip
                                        title={helper}
                                        arrow
                                        placement="top"
                                        disableHoverListener={!showError}
                                      >
                                        <TextField
                                          size="small"
                                          fullWidth
                                          value={raw ?? ""}
                                          onChange={(e) =>
                                            handleCellChange(idx, c.key, e.target.value)
                                          }
                                          error={showError}
                                          helperText={showError ? helper : ""}
                                          sx={commonSx}
                                          disabled={isSubmitting}
                                        />
                                      </Tooltip>
                                    );
                                  })()
                                ) : raw === null ||
                                  raw === undefined ||
                                  String(raw).trim() === "" ? (
                                  "—"
                                ) : (
                                  String(raw)
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Grid container spacing={2} sx={{ pt: "2rem" }}>
              <Grid size={2} />
              <Grid size={4}>
                <PageButtonWhite
                  width="12rem"
                  disabled={isSubmitting}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setRows([]);
                    setErrors([]);
                    setMissingCols([]);
                    setSubmitStatus(null);
                    setSubmitMessage("");
                    setApiResult(null);
                    fileRef.current?.clear();
                  }}
                >
                  Cancelar
                </PageButtonWhite>
              </Grid>
              <Grid size={4}>
                <PageButtonBlack
                  width="12rem"
                  disabled={!allValid || isSubmitting}
                  justifyContent="center"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSubmit();
                  }}
                >
                  Cargar archivo
                </PageButtonBlack>
              </Grid>
              <Grid size={2} />
            </Grid>
          </PageBgBlock>
        </Box>
      )}
    </PageBody>
  );
}
